import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { useCategories, useTriggerScrapeCoupangBatch, useBatchJobs } from '../../../apps/hooks/sourcing/useScrapeBatch';
import { CommandName } from '../../../infrastructure/BatchClient';

interface BatchFormData {
  task: string;
  category: string;
  subcategory: string;
  refresh: boolean;
  limit: number | null;
}

const ScrapeBatchPage: React.FC = () => {
  const [formData, setFormData] = useState<BatchFormData>({
    task: 'keywordSearch',
    category: '',
    subcategory: '',
    refresh: true,
    limit: null,
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useCategories();
  const triggerBatchMutation = useTriggerScrapeCoupangBatch();
  const { data: batchJobsData, isLoading: jobsLoading } = useBatchJobs({
    status: 'all',
    commandName: CommandName.SCRAPE_COUPANG,
  });

  const handleInputChange = (field: keyof BatchFormData, value: string | boolean | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      // Reset subcategory when category changes
      ...(field === 'category' && { subcategory: '' }),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.category) {
      setErrorMessage('Please select a category');
      setShowError(true);
      return;
    }

    if (!formData.subcategory) {
      setErrorMessage('Please select a subcategory');
      setShowError(true);
      return;
    }

    try {
      setShowError(false);
      setShowSuccess(false);

      const args: any = {
        task: formData.task,
        category: formData.category,
        refresh: formData.refresh,
      };

      if (formData.subcategory) {
        args.subcategory = formData.subcategory;
      }

      if (formData.limit !== null) {
        args.limit = formData.limit;
      }

      await triggerBatchMutation.mutateAsync(args);
      setShowSuccess(true);

      // Reset form
      setFormData({
        task: 'keywordSearch',
        category: '',
        subcategory: '',
        refresh: true,
        limit: null,
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit batch job');
      setShowError(true);
    }
  };

  const availableSubcategories = (formData.category && categoriesData?.subcategories[formData.category]) || [];

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'FAILED':
        return 'danger';
      case 'RUNNING':
      case 'STARTING':
        return 'primary';
      case 'PENDING':
      case 'SUBMITTED':
      case 'RUNNABLE':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (categoriesLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading categories...</span>
        </Spinner>
      </Container>
    );
  }

  if (categoriesError) {
    return (
      <Container>
        <Alert variant="danger">Failed to load categories. Please try again later.</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h4 className="mb-0">Scrape Coupang Batch Job</h4>
            </Card.Header>
            <Card.Body>
              {showSuccess && (
                <Alert variant="success" dismissible onClose={() => setShowSuccess(false)}>
                  Batch job submitted successfully! The job is now running in the background.
                </Alert>
              )}

              {showError && (
                <Alert variant="danger" dismissible onClose={() => setShowError(false)}>
                  {errorMessage}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Task Type</Form.Label>
                      <Form.Select
                        value={formData.task}
                        onChange={(e) => handleInputChange('task', e.target.value)}
                        required
                      >
                        <option value="keywordSearch">Keyword Search</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Limit</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="1000"
                        value={formData.limit || '500'}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleInputChange('limit', value ? parseInt(value) : null);
                        }}
                        placeholder="No limit (scrape all)"
                      />
                      <Form.Text className="text-muted">Leave empty to scrape all items without limit</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    required
                  >
                    <option value="">Select a category...</option>
                    {categoriesData?.categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subcategory *</Form.Label>
                  <Form.Select
                    value={formData.subcategory}
                    onChange={(e) => handleInputChange('subcategory', e.target.value)}
                    disabled={!formData.category || availableSubcategories.length === 0}
                    required
                  >
                    <option value="">Select a subcategory...</option>
                    {availableSubcategories.map((subcategory) => (
                      <option key={subcategory} value={subcategory}>
                        {subcategory}
                      </option>
                    ))}
                  </Form.Select>
                  {formData.category && availableSubcategories.length === 0 && (
                    <Form.Text className="text-muted">No subcategories available for this category.</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="refresh-checkbox"
                    label="Refresh existing data"
                    checked={formData.refresh}
                    onChange={(e) => handleInputChange('refresh', e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    <strong>Refresh enabled (recommended):</strong>
                    <br />
                    • Refresh CP Keyword Search stale data older than 30 days
                    <br />
                    • Find Glowpick products that were scraped but never processed for matching
                    <br />
                    • Won&apos;t overwrite matches that operators have already worked on
                    <br />
                    <strong>Refresh disabled:</strong> Only scrapes new products that haven&apos;t been scraped before,
                    skipping existing ones for faster processing
                  </Form.Text>
                </Form.Group>

                <div className="d-grid">
                  <Button type="submit" variant="primary" size="lg" disabled={triggerBatchMutation.isPending}>
                    {triggerBatchMutation.isPending ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Submitting...
                      </>
                    ) : (
                      'Submit Batch Job'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h4 className="mb-0">Recent Batch Jobs</h4>
            </Card.Header>
            <Card.Body>
              {jobsLoading ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading jobs...</span>
                </div>
              ) : batchJobsData?.jobs && batchJobsData.jobs.length > 0 ? (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchJobsData.jobs.slice(0, 10).map((job) => (
                      <tr key={job.jobId}>
                        <td>
                          <small className="text-monospace">{job.jobId.substring(0, 8)}...</small>
                        </td>
                        <td>
                          <Badge bg={getStatusBadgeVariant(job.status)}>{job.status}</Badge>
                        </td>
                        <td>
                          <small>{formatDate(job.createdAt)}</small>
                        </td>
                        <td>
                          <small>{job.startedAt ? formatDate(job.startedAt) : '-'}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No batch jobs found.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ScrapeBatchPage;
